import { FC, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { datadogLogs } from '@datadog/browser-logs';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { getPathWithRef } from 'helpers/utils';
import { getHubspotFormIds, getSiteConfigLoadStatus } from 'selectors/siteConfig';
import { getSiteConfig } from 'store/siteConfig/actions';
import { zoomForms } from './constants';

interface Props {
  form?: string;
  redirectHomeOnMissingForm?: boolean;
}

export const FormContent: FC<Props> = ({ form, redirectHomeOnMissingForm = false }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const instanceId = useId().replace(/:/g, '');
  const formContainerId = `hs-form-container-${form ?? 'unknown'}-${instanceId}`;
  const scriptLoaded = useRef(false);
  const formContainerRef = useRef<HTMLDivElement | null>(null);
  const formCreated = useRef(false);
  const [isSiteConfigRequestSettled, setIsSiteConfigRequestSettled] = useState(false);

  const hubspotFormIds = useAppSelector(getHubspotFormIds);
  const siteConfigLoadStatus = useAppSelector(getSiteConfigLoadStatus);
  const hubspotFormId = form ? hubspotFormIds?.[form] : undefined;
  // A boolean, not the map: a refetch replaces the map, and re-running the effect wipes the form.
  const hasHubspotFormsConfig = Boolean(hubspotFormIds);
  // Wait on our own request, not the store status: a failed request from an earlier page leaves a stale error behind.
  const isWaitingForSiteConfig = !hubspotFormId && !isSiteConfigRequestSettled;

  useEffect(() => {
    let isMounted = true;
    dispatch(getSiteConfig())
      .catch(() => undefined)
      .finally(() => {
        if (isMounted) setIsSiteConfigRequestSettled(true);
      });
    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  useEffect(() => {
    if (!form) {
      if (redirectHomeOnMissingForm) {
        navigate(getPathWithRef('/'));
      }
      return;
    }

    if (zoomForms[form]) {
      window.open(zoomForms[form], '_blank');
      navigate(getPathWithRef('/set?p=standup'));
      return;
    }

    if (!hubspotFormId) {
      if (isWaitingForSiteConfig) return;
      // Request failures are logged once in requestSiteConfig.
      if (siteConfigLoadStatus !== 'failed' && !hasHubspotFormsConfig) {
        datadogLogs.logger.error(
          'Forms page: hubspot_forms site config row is missing or malformed'
        );
      }
      if (redirectHomeOnMissingForm) {
        navigate(getPathWithRef('/'));
      }
      return;
    }

    const formContainer = formContainerRef.current;
    if (!formContainer) return;

    // Clear any existing form
    formContainer.innerHTML = '';

    if (!formCreated.current) {
      // Check if HubSpot script is already loaded
      if (window.hbspt) {
        window.hbspt.forms.create({
          region: 'na1',
          portalId: '43762772',
          formId: hubspotFormId,
          target: `#${formContainerId}`,
        });
        formCreated.current = true;
      } else if (!scriptLoaded.current) {
        const script = document.createElement('script');
        script.src = '//js.hsforms.net/forms/embed/v2.js';
        script.async = true;

        script.onload = () => {
          if (window.hbspt && !formCreated.current) {
            window.hbspt.forms.create({
              region: 'na1',
              portalId: '43762772',
              formId: hubspotFormId,
              target: `#${formContainerId}`,
            });
            formCreated.current = true;
            scriptLoaded.current = true;
          }
        };

        document.body.appendChild(script);
      }
    }

    // Cleanup function
    return () => {
      if (formContainer) {
        formContainer.innerHTML = '';
      }
    };
  }, [
    form,
    formContainerId,
    navigate,
    hubspotFormId,
    hasHubspotFormsConfig,
    isWaitingForSiteConfig,
    siteConfigLoadStatus,
    redirectHomeOnMissingForm,
  ]);

  return <div id={formContainerId} ref={formContainerRef} className="hubspot-container" />;
};
